import { Component, computed, inject, ViewChild, output } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonIcon,
  IonPopover,
  IonItem,
  IonLabel,
  IonTitle,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  logInOutline,
  logOutOutline,
  homeOutline,
  listOutline,
  personAddOutline,
  settingsOutline,
  menuOutline,
  documentTextOutline,
  barChartOutline,
} from 'ionicons/icons';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';

import { NavigationService } from '@core/services/navigation.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-header-mast',
  standalone: true,
  imports: [
    IonHeader,
    IonToolbar,
    IonButton,
    RouterLink,
    RouterLinkActive,
    IonIcon,
    IonPopover,
    IonItem,
    IonLabel,
    IonTitle,
  ],
  templateUrl: './header-mast.component.html',
  styleUrls: ['./header-mast.component.scss'],
})
export class HeaderMastComponent {
  private readonly router = inject(Router);
  readonly navigationService = inject(NavigationService);

  readonly showNonprodWarning = environment.showNonprodWarning;

  @ViewChild('saypPopover') saypPopover!: IonPopover;

  toggleMenu = output<void>();

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      startWith(this.router.url)
    ),
    { initialValue: this.router.url }
  );

  readonly isSaypRouteActive = computed(
    () => this.currentUrl()?.startsWith('/save-as-you-pay') ?? false
  );

  readonly activeModule = computed(() => {
    const url = this.currentUrl();

    for (const module of Object.values(this.moduleTitles)) {
      if (url?.startsWith(module.rootPath)) {
        return module;
      }
    }
    return null;
  });

  private readonly moduleTitles = {
    login: { key: 'login', full: 'Login', short: 'Login', rootPath: '/login' },
    unauthorized: {
      key: 'unauthorized',
      full: 'Unauthorized',
      short: 'Unauthorized',
      rootPath: '/unauthorized',
    },
    dashboard: {
      key: 'dashboard',
      full: 'Dashboard',
      short: 'Home',
      rootPath: '/dashboard',
    },
    sayp: {
      key: 'sayp',
      full: 'Save As You Pay',
      short: 'SAYP',
      rootPath: '/save-as-you-pay',
    },
  };

  constructor() {
    addIcons({
      logInOutline,
      logOutOutline,
      homeOutline,
      listOutline,
      personAddOutline,
      settingsOutline,
      menuOutline,
      documentTextOutline,
      barChartOutline,
    });
  }

  navigateToHome(): void {
    this.navigationService.navigateTo('/dashboard');
  }

  signIn(): void {
    this.navigationService.signIn();
  }

  signOut(): void {
    this.navigationService.signOut();
  }

  navigateToSaypRoute(path: string): void {
    this.navigationService.navigateTo(path);
    this.saypPopover?.dismiss();
  }

  onToggleMobileMenu(): void {
    this.toggleMenu.emit();
  }
}
